import {
  followUpAnswerMap,
  followUpTargets,
  type TrainingAnswer,
  type TrainingAnswers,
  type TrainingPlayerFollowUp,
  type TrainingPlayerPage,
} from '@hcc/types';
import { ShortTextAnswer } from './answers/ShortTextAnswer';

/**
 * A follow-up page: looks back over the answers given so far, shows every
 * question whose chosen option matched (e.g. each "Yes"), and asks for an
 * explanation under each. The explanations live in ONE answer under the
 * page id, a map of source question id to text.
 */
export function FollowUpSlide({
  page,
  pages,
  answers,
  onChange,
  disabled,
}: {
  page: TrainingPlayerFollowUp;
  pages: TrainingPlayerPage[];
  answers: TrainingAnswers;
  onChange: (next: TrainingAnswer) => void;
  disabled?: boolean;
}) {
  const targets = followUpTargets(pages, answers, page);
  const given = followUpAnswerMap(answers[page.id]);
  const multiline = page.multiline !== false;

  return (
    <div className="space-y-5">
      {page.title ? <h2 className="text-lg font-semibold text-fg">{page.title}</h2> : null}
      {page.intro ? <p className="text-sm text-fg-muted">{page.intro}</p> : null}
      {targets.length === 0 ? (
        <p className="rounded-token-sm bg-surface-2 px-4 py-3 text-sm text-fg-muted">
          Nothing to expand on: none of your answers so far were &ldquo;{page.match_label.trim()}
          &rdquo;. Carry on to the next page.
        </p>
      ) : (
        <ol className="space-y-5">
          {targets.map((t) => {
            const labelId = `fu-${page.id}-${t.question_id}`;
            return (
              <li key={t.question_id} className="space-y-2">
                <p id={labelId} className="text-sm font-medium text-fg">
                  {t.prompt}
                  <span className="ml-2 text-xs font-normal text-fg-dim">
                    You answered &ldquo;{t.answer_label}&rdquo;
                  </span>
                  {page.optional ? (
                    <span className="ml-1.5 text-xs font-normal text-fg-dim">(optional)</span>
                  ) : null}
                </p>
                <ShortTextAnswer
                  value={given[t.question_id] ?? ''}
                  onChange={(next) => onChange({ ...given, [t.question_id]: next })}
                  disabled={disabled}
                  ariaLabelledBy={labelId}
                  multiline={multiline}
                  placeholder={page.placeholder ?? 'Please tell us more'}
                />
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
